"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, ArrowRight, Loader2, ArrowLeft } from "lucide-react";
import { AGENCY_PROFILE_STORAGE_KEY, BASE_URL } from "../common/constant";


export default function Login() {
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!email || !password) {
            setError("Please enter your email and password.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/agency/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "*/*",
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                const msg =
                    data?.detail ||
                    data?.message ||
                    data?.error ||
                    "Invalid email or password.";
                setError(typeof msg === "string" ? msg : JSON.stringify(msg));
                return;
            }

            // Response shape: { status, message, data: { token, agency: { ... } } }
            const token = data?.data?.token;
            const agency = data?.data?.agency;

            if (!token) {
                setError("Login succeeded but no token was returned. Please try again.");
                return;
            }

            // Save token — used by API calls throughout the app
            localStorage.setItem("access_token", token);

            // Cache agency profile so sidebar can read name/email instantly
            // without waiting for a separate /profile API call
            if (agency) {
                localStorage.setItem(AGENCY_PROFILE_STORAGE_KEY, JSON.stringify(agency));
            }

            router.push("/dashboard");
        } catch (err) {
            setError("Network error. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Left panel - illustration */}
            <div
                className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative"
                style={{
                    backgroundImage: "url('/banner.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
                <div className="relative z-10 flex flex-col justify-between h-full">
                    <Link href="/" className="inline-flex">
                        <div className="bg-white/80 backdrop-blur-xl border border-white/40 rounded-2xl p-3 shadow-2xl flex items-center justify-center transition-all hover:bg-white/90">
                            <img
                                src="/logo2.png"
                                alt="Medtechdata"
                                className="h-10 w-auto object-contain"
                            />
                        </div>
                    </Link>

                    <div className="space-y-6">
                        <h2 className="text-4xl font-semibold text-white leading-tight">
                            Your gateway to premium agency data
                        </h2>
                        <p className="text-white/80 text-lg">
                            Access 14.7M+ verified records, powerful APIs, and real-time
                            analytics — all in one place.
                        </p>
                        <div className="flex flex-col gap-3">
                            {[
                                "500+ curated datasets ready to use",
                                "Sub-100ms API response times",
                                "Enterprise-grade security & compliance",
                            ].map((f) => (
                                <div key={f} className="flex items-center gap-3">
                                    <div className="w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                                        <ArrowRight className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="text-white/95 text-sm font-medium">{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-black/30 backdrop-blur-md rounded-2xl p-5 border border-white/10">
                        <p className="text-white/95 text-sm italic">
                            "Medtechdata transformed how we source and integrate data. What
                            used to take weeks now takes hours."
                        </p>
                        <div className="flex items-center gap-3 mt-4">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                MK
                            </div>
                            <div>
                                <p className="text-white text-sm font-medium">Marcus Kim</p>
                                <p className="text-white/70 text-xs">
                                    VP Engineering, DataBridge
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-[#f7f7f5]">
                <div className="w-full max-w-sm">
                    {/* Top: Prominent Logo */}
                    <div className="flex justify-center mb-6">
                        <Link href="/">
                            <img
                                src="/logo2.png"
                                alt="Medtechdata"
                                className="h-10 sm:h-12 w-auto object-contain"
                            />
                        </Link>
                    </div>

                    {/* Back to Home Button */}
                    <div className="mb-6">
                        <Link
                            href="/"
                            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors group"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <span>Back to home</span>
                        </Link>
                    </div>

                    <div className="mb-8">
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Welcome back
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Sign in to your account to continue
                        </p>
                    </div>

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <label htmlFor="email" className="text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <input
                                id="email"
                                type="email"
                                placeholder="james@agency.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <a
                                    href="#"
                                    className="text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors"
                                >
                                    Forgot password?
                                </a>
                            </div>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="w-full rounded-xl h-11 px-3 pr-10 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 mt-2 rounded-xl font-medium text-white text-sm transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center"
                            style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" }}
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    Signing in…
                                </span>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </form>

                    <div className="my-6 flex items-center gap-4">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400">or continue with</span>
                        <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* <div className="grid grid-cols-2 gap-3">
                        {["Google", "GitHub"].map((provider) => (
                            <button
                                key={provider}
                                type="button"
                                className="rounded-xl h-11 font-medium text-sm text-gray-700 border border-gray-200 bg-white hover:bg-orange-50 hover:border-orange-200 hover:text-orange-600 transition-all"
                            >
                                {provider}
                            </button>
                        ))}
                    </div> */}

                    <p className="mt-6 text-center text-sm text-gray-500">
                        Don&apos;t have an account?{" "}
                        <Link
                            href="/signup"
                            className="font-medium text-orange-500 hover:text-orange-600 transition-colors"
                        >
                            Sign up free
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}